import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { useChiudiAltreSessioni, useProfilo } from '../api/profilo.js';
import { type Sessione, usePreferenze } from '../api/sessione.js';
import { SchedaPassword } from '../componenti/SchedaPassword.js';
import { SeApre } from '../componenti/SeApre.js';
import { etichettaLivello } from '../etichette.js';
import { dataOra } from '../formati.js';
import { type Lingua, cambiaLingua, lingua, t } from '../i18n/lingua.js';
import { type Aspetto, applicaAspetto } from '../tema.js';

/**
 * Il profilo.
 *
 * Nel v2 questa pagina non c'è: non esiste modo di cambiarsi la password, di
 * scegliere la lingua, né di sapere da dove si è entrati l'ultima volta.
 * Nella 3.0 aspetto e lingua si toccavano già dal menu in alto a destra, e
 * quello resta — è comodo — ma un interruttore nascosto dentro un menu non è
 * un posto dove qualcuno va a cercare la propria password.
 *
 * La pagina risponde a quattro domande, in quest'ordine: chi sono, come voglio
 * vedere il programma, come cambio la password, chi sta usando la mia utenza.
 */

const ASPETTI: { valore: Aspetto; segno: string; etichetta: string }[] = [
  { valore: 'light', segno: '☀', etichetta: t('Chiaro') },
  { valore: 'dark', segno: '☾', etichetta: t('Scuro') },
  { valore: 'system', segno: '◐', etichetta: t('Come il sistema') },
];

const LINGUE: { valore: Lingua; etichetta: string }[] = [
  { valore: 'it', etichetta: t('Italiano') },
  { valore: 'en', etichetta: t('Inglese') },
];

/**
 * Le pagine da cui si può cominciare, col nome che hanno nel menu.
 *
 * **Quali siano lo decide il servizio** (`crm-api/src/preferenze/schermate.ts`)
 * e lo dice nella risposta: qui c'è solo come si chiamano. Così se una pagina
 * lì non c'è più, di qui non la si può nemmeno scegliere.
 */
const NOMI: { valore: string; etichetta: string }[] = [
  { valore: '/', etichetta: t('Il mio lavoro') },
  { valore: '/sales-orders', etichetta: t('Ordini di vendita') },
  { valore: '/alerts', etichetta: t('Avvisi') },
  { valore: '/customers', etichetta: t('Clienti') },
  { valore: '/items', etichetta: t('Articoli') },
  { valore: '/price-lists', etichetta: t('Listini') },
  { valore: '/negotiations', etichetta: t('Trattative') },
  { valore: '/documents', etichetta: t('Documenti') },
];

export function Profilo({ sessione }: { sessione: Sessione }) {
  const profilo = useProfilo();
  const preferenze = usePreferenze();
  const chiudiAltre = useChiudiAltreSessioni();
  const [chiuse, setChiuse] = useState<number | null>(null);

  const utente = sessione.user;
  const inUso = lingua();
  const azienda = utente.companies.find((a) => a.id === sessione.companyId);

  /**
   * L'aspetto si vede subito e poi si salva: se la scrittura non riesce la
   * scelta vale comunque per questa sessione. È una preferenza, non un dato da
   * difendere.
   */
  const scegliAspetto = (valore: Aspetto) => {
    applicaAspetto(valore);
    preferenze.mutate({ theme: valore });
  };

  const scegliLingua = (valore: Lingua) => {
    if (valore === inUso) return;
    preferenze.mutate({ language: valore });
    cambiaLingua(valore);
  };

  const errore = preferenze.error instanceof ErroreApi ? preferenze.error.message : null;
  const altre = (profilo.data?.sessions ?? []).filter((s) => !s.corrente);
  const ammesse = profilo.data?.homeScreens;
  const schermate = ammesse ? NOMI.filter((s) => ammesse.includes(s.valore)) : NOMI;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Profilo')}</h1>
          <div className="page-sub">
            {utente.name} · {etichettaLivello(utente.accessLevel)}
            {azienda ? ` · ${azienda.name}` : ''}
          </div>
        </div>
      </div>

      {errore && (
        <div className="avviso" role="alert" style={{ margin: '0 20px 12px' }}>
          <span className="avviso-segno">▲</span>
          <span>{errore}</span>
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Chi sono')}</span>
          </div>
          <div className="card-body">
            <div className="fields">
              <div className="field">
                <label>{t('Nome')}</label>
                <div className="readonly">{utente.name}</div>
              </div>
              <div className="field">
                <label>{t('Indirizzo con cui entro')}</label>
                <div className="readonly">{utente.email}</div>
              </div>
              <div className="field">
                <label>{t('Livello di accesso')}</label>
                <div className="readonly">{etichettaLivello(utente.accessLevel)}</div>
              </div>
              <div className="field">
                <label>{t('Aziende in cui lavoro')}</label>
                <div className="readonly">
                  {utente.companies.map((a) => (
                    <span key={a.id} className="badge" title={a.name}>
                      {a.code}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            {/* Nome e indirizzo stanno sull'anagrafica, che è dove si
                cambiano: qui si leggono e basta, così la stessa cosa non si
                scrive in due posti. */}
            <p className="dim" style={{ fontSize: 13, marginBottom: 0, marginTop: 12 }}>
              {t('Il nome e l’indirizzo si cambiano dalla scheda dell’operatore.')}{' '}
              <SeApre
                sezione="operators"
                altrimenti={t('Per cambiarli chiedi a un amministratore.')}
              >
                <Link to="/operators/$id" params={{ id: String(utente.id) }}>
                  {t('Apri la mia scheda')}
                </Link>
              </SeApre>
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Come si vede il programma')}</span>
          </div>
          <div className="card-body">
            <div className="fields">
              <div className="field span-2">
                <label>{t('Aspetto')}</label>
                <div className="scelte">
                  {ASPETTI.map((voce) => (
                    <button
                      key={voce.valore}
                      className="preset"
                      aria-pressed={utente.theme === voce.valore}
                      onClick={() => scegliAspetto(voce.valore)}
                    >
                      {voce.segno} {t(voce.etichetta)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="field span-2">
                <label>{t('Lingua')}</label>
                <div className="scelte">
                  {LINGUE.map((voce) => (
                    <button
                      key={voce.valore}
                      className="preset"
                      aria-pressed={inUso === voce.valore}
                      onClick={() => scegliLingua(voce.valore)}
                    >
                      {voce.valore.toUpperCase()} {t(voce.etichetta)}
                    </button>
                  ))}
                </div>
                <span className="hint">{t('Cambiare lingua ricarica la pagina.')}</span>
              </div>

              <div className="field span-2">
                <label htmlFor="pr-schermata">{t('Da dove comincio quando entro')}</label>
                <select
                  className="select"
                  id="pr-schermata"
                  value={utente.homeScreen ?? '/'}
                  onChange={(e) =>
                    preferenze.mutate({
                      // «Il mio lavoro» è il comportamento normale, e a
                      // database si scrive come nessuna scelta
                      homeScreen: e.target.value === '/' ? null : e.target.value,
                    })
                  }
                >
                  {schermate.map((s) => (
                    <option key={s.valore} value={s.valore}>
                      {t(s.etichetta)}
                    </option>
                  ))}
                </select>
                <span className="hint">
                  {t('Vale dal prossimo accesso, e ogni volta che si riapre il programma.')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <SchedaPassword />

        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Sessioni aperte')}</span>
          </div>
          <div className="card-body">
            {profilo.isPending && <div className="dim">{t('Un momento…')}</div>}
            {profilo.data && (
              <>
                <dl className="kv">
                  {profilo.data.sessions.map((s) => (
                    <div key={s.apertaIl + (s.ipAddress ?? '')} style={{ display: 'contents' }}>
                      <dt title={s.userAgent ?? undefined}>
                        {dataOra(s.ultimoUsoIl)}
                        {s.corrente && (
                          <span className="badge badge-ok" style={{ marginLeft: 6 }}>
                            {t('questa')}
                          </span>
                        )}
                      </dt>
                      <dd className="mono">{s.ipAddress ?? '—'}</dd>
                    </div>
                  ))}
                </dl>

                {altre.length > 0 && (
                  <button
                    className="btn"
                    style={{ marginTop: 12 }}
                    disabled={chiudiAltre.isPending}
                    onClick={() =>
                      chiudiAltre.mutate(undefined, { onSuccess: (e) => setChiuse(e.closed) })
                    }
                  >
                    {chiudiAltre.isPending ? t('Chiudo…') : t('Esci dagli altri dispositivi')}
                  </button>
                )}
                {chiuse !== null && (
                  <p className="dim" style={{ fontSize: 13, marginBottom: 0, marginTop: 8 }}>
                    {chiuse === 0
                      ? t('Non c’era nessun’altra sessione aperta.')
                      : t('Chiuse {quante} sessioni. Questa resta aperta.', { quante: chiuse })}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">{t('Ultimi accessi')}</span>
          </div>
          <div className="card-body">
            {profilo.isPending && <div className="dim">{t('Un momento…')}</div>}
            {profilo.data &&
              (profilo.data.logins.length === 0 ? (
                <div className="dim" style={{ fontSize: 13 }}>
                  {t('Nessun accesso registrato.')}
                </div>
              ) : (
                <dl className="kv">
                  {profilo.data.logins.map((a) => (
                    <div key={a.quando} style={{ display: 'contents' }}>
                      <dt title={a.userAgent ?? undefined}>{dataOra(a.quando)}</dt>
                      <dd className="mono">{a.ipAddress ?? '—'}</dd>
                    </div>
                  ))}
                </dl>
              ))}
            {/* Serve a una cosa sola, ed è quella che conta: accorgersi di un
                accesso che non è tuo. */}
            <p className="dim" style={{ fontSize: 13, marginBottom: 0, marginTop: 12 }}>
              {t('Se qui c’è un accesso che non hai fatto tu, cambia subito la password e dillo a un amministratore.')}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
